import React, { useEffect, useState, useCallback } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import api from '@/lib/api';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { ROLES } from '@/lib/constants';
import { RoleBadge } from '@/components/common/RoleBadge';
import { toast } from 'sonner';
import { Pencil, Trash2, UserPlus } from 'lucide-react';

const EMPTY_FORM = { email: '', name: '', role: 'software_intern', password: '', expires_at: '' };

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/users');
      setUsers(data);
    } catch (err) {
      toast.error('Failed to load users');
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(EMPTY_FORM); setOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({
      email: u.email,
      name: u.name,
      role: u.role,
      password: '',
      expires_at: u.expires_at ? u.expires_at.slice(0, 16) : '',
      is_active: u.is_active,
    });
    setOpen(true);
  };

  const submit = async () => {
    try {
      const body = { ...form };
      if (body.expires_at) body.expires_at = new Date(body.expires_at).toISOString();
      else body.expires_at = null;
      if (!body.password) delete body.password;
      if (editing) {
        await api.patch(`/users/${editing.user_id}`, body);
        toast.success('User updated');
      } else {
        await api.post('/users', body);
        toast.success('User created');
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  const toggleActive = async (u, v) => {
    try { await api.patch(`/users/${u.user_id}`, { is_active: v }); load(); } catch { toast.error('Failed'); }
  };

  const remove = async (u) => {
    if (!confirm(`Delete ${u.name}?`)) return;
    try { await api.delete(`/users/${u.user_id}`); toast.success('Deleted'); load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <AppShell title="User Management" actions={<Button onClick={openNew} data-testid="admin-users-create-button"><UserPlus className="h-4 w-4 mr-2" />Add user</Button>}>
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead className="bg-secondary/60">
              <tr><th className="text-left p-3">Name</th><th className="text-left p-3">Email</th><th className="text-left p-3">Role</th><th className="text-left p-3">Expires</th><th className="text-left p-3">Active</th><th></th></tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id} className="border-t hover:bg-accent/40" data-testid="admin-users-row">
                  <td className="p-3 font-medium">{u.name}</td>
                  <td className="p-3 text-muted-foreground">{u.email}</td>
                  <td className="p-3"><RoleBadge role={u.role} /></td>
                  <td className="p-3" data-testid="admin-users-expiration-cell">{u.expires_at ? u.expires_at.slice(0, 10) : '—'}</td>
                  <td className="p-3"><Switch checked={u.is_active} onCheckedChange={(v) => toggleActive(u, v)} data-testid="admin-users-active-switch" /></td>
                  <td className="p-3 flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(u)} data-testid="admin-users-edit-button" aria-label="Edit"><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(u)} data-testid="admin-users-delete-button" aria-label="Delete"><Trash2 className="h-3.5 w-3.5" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="user-form-dialog">
          <DialogHeader><DialogTitle className="font-display">{editing ? 'Edit user' : 'Create user'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-form-name" /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!editing} data-testid="user-form-email" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-form-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Expiration</Label><Input type="datetime-local" value={form.expires_at || ''} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} data-testid="user-form-expires" /></div>
            </div>
            <div><Label>{editing ? 'New password (leave blank to keep)' : 'Password'}</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-form-password" placeholder="Enter a strong password" autoComplete="new-password" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={!form.name || !form.email} data-testid="user-form-submit">{editing ? 'Save' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
